import React, { useEffect, useState } from 'react'
import {
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Add, AutoAwesome, Search } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { api } from '../../../../services/Main/Api'
import PaginationGlobal from '../../../../components/Shared/PaginationGlobal'
import NoResultsAnimation from '../../../../components/Shared/animations/NoResultsAnimation'
import moment from 'moment'

const providerLabels = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
}

const AIProviderConfigList = () => {
  const navigate = useNavigate()

  const [response, setResponse] = useState(null)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({ page: 1, page_size: 50 })

  const fetchData = async () => {
    setLoading(true)
    try {
      const params = {
        page: filters.page,
        page_size: filters.page_size,
        ...(search && { search }),
      }
      const res = await api.get('/ai/provider-configs/', { params })
      setResponse(res.data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1 }))
    }, 1000)
    return () => clearTimeout(timer)
  }, [search])

  return (
    <>
      <Helmet>
        <title>Provedores de IA - Admin</title>
      </Helmet>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight="bold">
            Provedores de IA
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/admin/ai-provider-config/create')}
          >
            Nova Configuração
          </Button>
        </Box>

        <Card>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Pesquisar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {response === null ? (
            <Skeleton variant="rounded" height={400} animation="pulse" sx={{ m: 2 }} />
          ) : response?.results?.length === 0 ? (
            <Box sx={{ p: 4 }}>
              <NoResultsAnimation />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Provider</TableCell>
                      <TableCell>Modelo</TableCell>
                      <TableCell>Ativo</TableCell>
                      <TableCell>Criado em</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {response?.results?.map((config) => (
                      <TableRow
                        key={config.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/admin/ai-provider-config/${config.id}`)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AutoAwesome fontSize="small" color="primary" />
                            {config.name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={providerLabels[config.provider] || config.provider}
                            size="small"
                            color={config.provider === 'openai' ? 'success' : 'info'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {config.default_model || 'Padrão'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={config.is_active ? 'Ativo' : 'Inativo'}
                            size="small"
                            color={config.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {moment(config.created_at).format('DD/MM/YYYY')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {response?.count > 0 && (
                <PaginationGlobal
                  state={response}
                  filters={filters}
                  setFilters={setFilters}
                />
              )}
            </>
          )}
        </Card>
      </Container>
    </>
  )
}

export default AIProviderConfigList
